import React, { memo, useEffect, useMemo, useState } from 'react';
import ReactPaginate, { ReactPaginateProps } from 'react-paginate';

import { Button, Icons, Input, Select } from '@components/common';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { EInputType } from '@components/common/Input/Input.types';

import { pageSizeOptions } from '@components/common/Pagination/constants';

import styles from './Pagination.module.scss';

export type TOnPageChangeCallback = ReactPaginateProps['onPageChange']

interface IPaginationProps {
	currentPage?: number;
	totalItems: number;
	pageSize?: number;
	onPageChange?: (page: number) => void;
	onPageSizeChange?: (pageSize: number) => void;
	hideControls?: boolean;
	scrollToTop?: () => void;
	resetOrder?: () => void;
	hasCustomOrder?: boolean;
}

const JUMP_TO_THRESHOLD = 5;

const Pagination = ({
	totalItems,
	currentPage = 0,
	onPageChange,
	onPageSizeChange,
	scrollToTop,
	pageSize = 0,
	hideControls = false,
	resetOrder,
	hasCustomOrder = false,
}: IPaginationProps) => {
	const [pageCount, setPageCount] = useState(1);
	const [selectedPageSize, setSelectedPageSize] = useState<TSelectOption | null>(
		pageSizeOptions.find(opt => opt.value === pageSize) || null,
	);
	const [jumpTo, setJumpTo] = useState('');
	const [isJumpToVisible, setIsJumpToVisible] = useState(false);

	useEffect(() => {
		setSelectedPageSize(pageSizeOptions.find(opt => opt.value === pageSize) || null);
	}, [pageSize]);

	const resultsText = useMemo(() => {
		const start = ((currentPage === 1 || currentPage === null) ? 0 : currentPage - 1) * pageSize + 1;
		const end = Math.min(start + pageSize - 1, totalItems);

		return hideControls ? `${totalItems} results` : `${start} - ${end} of ${totalItems} results`;
	}, [currentPage, totalItems, pageSize, hideControls]);

	useEffect(() => {
		if (!pageSize) {
			return;
		}

		setPageCount(Math.ceil(totalItems / pageSize));
	}, [totalItems, pageSize]);

	const handlePageChange: TOnPageChangeCallback = (value: { selected: number }) => {
		onPageChange?.(value.selected + 1);
		scrollToTop?.();
	};

	const handlePageSizeChange = (option: TSelectOption) => {
		setSelectedPageSize(option);

		if (option?.value && onPageSizeChange) {
			onPageSizeChange(+option.value);
		}
	};

	const handleJumpToChange = (value: string) => {
		const formattedValue = value
			.replace(/[^0-9]/g, '')
			.replace(/^0+/, '');

		setJumpTo(formattedValue);
	};

	useEffect(() => {
		setIsJumpToVisible(pageCount > JUMP_TO_THRESHOLD);
	}, [pageCount]);

	useEffect(() => {
		setJumpTo('');
	}, [currentPage]);

	const handleJumpToPage = (page: string) => {
		onPageChange?.(+page);
	};

	return (
		<div
			className={styles.paginationContainer}
			data-testid='pagination-container'
		>
			<div className={styles.interval}>{resultsText}</div>
			{!hideControls
				&& <>
					<div className={styles.spacer}>
					</div>
					{hasCustomOrder && (
						<div className={styles.resetOrderContainer}>
							<Button
								onClick={resetOrder}
								secondary
							>
								Reset Order
							</Button>
						</div>
					)}
					<div className={styles.pageSize}>
						<span>Show:</span>
						<Select
							handleChange={handlePageSizeChange}
							isPagination
							isWithoutUseForms
							menuPlacement='top'
							name='pageSize'
							options={pageSizeOptions}
							value={selectedPageSize}
						/>
					</div>
					{isJumpToVisible && (
						<div className={styles.jumpToContainer}>
							<span>Jump to:</span>
							<Input
								containerClassName={styles.inputContainer}
								name='pageJump'
								onChange={handleJumpToChange}
								onEnterKeyDown={handleJumpToPage}
								placeholder='page'
								regexValidation={/[^0-9]/g}
								type={EInputType.float}
								value={jumpTo}
								withUseForms={false}
							/>
						</div>
					)}
					<ReactPaginate
						activeLinkClassName={styles.active}
						breakLabel='...'
						containerClassName={styles.pagination}
						forcePage={currentPage - 1}
						marginPagesDisplayed={1}
						nextClassName={styles.arrows}
						nextLabel={Icons.chevronRight()}
						onPageChange={handlePageChange}
						pageCount={pageCount}
						pageRangeDisplayed={3}
						previousClassName={styles.arrows}
						previousLabel={Icons.chevronLeft()}
					/>
				</>
			}
		</div>
	);
};

export default memo(Pagination);
